import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { RumCount, RumRollup } from '@/api/types';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { ClickHeatmap } from '@/components/ClickHeatmap';
import { Select } from '@/components/Select';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { useRumLive } from '@/hooks/useRumLive';
import { AnalyticsLoadError } from '@/components/AnalyticsLoadError';

/**
 * Realtime behaviour, from the control plane's own tracker.
 *
 * <b>Why this page exists.</b> Microsoft Clarity has no realtime API — its export serves 1–3 day-old
 * aggregates on a 10-request daily budget — and GA4's realtime report gives active users but no
 * behaviour. Neither can answer "is the new CTA working, right now". The first-party tracker can,
 * and it also captures signals that need DOM access: dead clicks, form abandonment, which section
 * was actually read.
 *
 * <b>The split on this page is deliberate and labelled.</b> The top half is a volatile 30-minute
 * window held in memory — a restart clears it, which is fine. The bottom half is the durable day and
 * month rollups, which are NOT realtime by design: they are written every 30 seconds and are what
 * survives. Presenting the two without saying which is which would invite someone to read a
 * half-collected day as a decline.
 */
export function LivePage({ siteKey }: { siteKey: string }) {
  const [pageFilter, setPageFilter] = useState<string>('');
  const { data, connected, error } = useRumLive(siteKey, pageFilter || undefined);

  const rollups = useQuery({
    queryKey: ['rum-rollups', siteKey, 'day'],
    queryFn: () => api.rumRollups(siteKey, 'day'),
    // Durable data changes on the flush interval; polling faster only burns queries.
    refetchInterval: 60_000,
    retry: false,
  });

  const days = rollups.data ?? [];
  const today = days.at(-1);

  const pageOptions = useMemo(
    () => (data?.top_pages ?? []).map((p) => p.value).filter(Boolean),
    [data?.top_pages],
  );

  const hasLiveTraffic = (data?.events ?? 0) > 0;

  return (
    <>
      <div className="live-header">
        <h1 className="t-title" style={{ margin: 0 }}>Live</h1>
        <span className={`live-dot ${connected ? 'is-live' : 'is-stale'}`} aria-hidden />
        <span className="t-label">
          {connected ? 'Streaming' : 'Reconnecting — showing polled data'}
        </span>
        {data && (
          <span className="t-label subtle">
            last {data.window_minutes} min · updated {new Date(data.as_of).toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && !data && <Callout tone="error" title="Live data unavailable">{error}</Callout>}

      {!hasLiveTraffic && !error && (
        <Callout tone="info" title="No activity in the last 30 minutes">
          The tracker reports within seconds of a visit. If the landing page has traffic and nothing
          appears here, check that <code>rum.js</code> is loaded and that this origin is listed in{' '}
          <code>ControlPlane:Rum:AllowedOrigins</code>.
        </Callout>
      )}

      <div className="tile-grid">
        <StatTile label="Active sessions" value={fmt(data?.active_sessions)} sub="last 30 min" />
        <StatTile label="Page views" value={fmt(data?.page_views)} sub="last 30 min" />
        <StatTile label="Clicks" value={fmt(data?.clicks)} sub="last 30 min" />
        <StatTile
          label="Frustrations / session"
          value={data?.frustrations_per_session != null ? data.frustrations_per_session.toFixed(2) : '—'}
          // Spelled out because the number is not a percentage and 1.4 is a legitimate value.
          sub={data ? `${data.frustrations} rage/dead clicks etc. ÷ sessions` : 'rage/dead clicks etc. ÷ sessions'}
        />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Click map</h2>
          {/* Unfiltered merges every page's clicks into one picture of nothing — two different
              layouts averaged. Offered, but the hint says why it is the weaker choice. */}
          <Select
            label="Page"
            value={pageFilter}
            onChange={setPageFilter}
            placeholder="All pages (merged)"
            options={pageOptions.map((path) => ({ value: path, label: path }))}
            hint={pageFilter ? undefined : 'Merged maps average two layouts together'}
            inline
          />
        </div>
        <div className="card-body">
          {data ? (
            <ClickHeatmap cells={data.heatmap} grid={data.heat_grid} />
          ) : (
            <div className="empty-state t-body">Waiting for the first frame…</div>
          )}
        </div>
      </div>

      <div className="grid-2">
        <Panel title="Active pages" rows={data?.top_pages} />
        <Panel title="What people clicked" rows={data?.top_targets} />
        <Panel title="Countries" rows={data?.by_country} />
        <Panel title="Event types" rows={data?.by_kind} />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Stored history</h2>
          <span className="t-label subtle">
            written every 30s — not realtime, and the copy that survives a restart
          </span>
        </div>
        <div className="card-body">
          {rollups.isError && !rollups.data ? (
            <AnalyticsLoadError error={rollups.error} what="the stored history" />
          ) : days.length === 0 ? (
            <div className="empty-state t-body">
              No days recorded yet. The first rollup appears within a minute of the first visit.
            </div>
          ) : (
            <>
              <div className="tile-grid">
                <StatTile label="Sessions today" value={fmt(today?.sessions)} sub={today?.period_start} />
                <StatTile
                  label="Avg scroll depth"
                  value={today?.average_scroll_depth != null ? `${Math.round(today.average_scroll_depth)}%` : '—'}
                  sub="— means not measured, not 0%"
                />
                <StatTile
                  label="Form completion"
                  value={today?.form_completion_rate != null ? `${Math.round(today.form_completion_rate * 100)}%` : '—'}
                  sub={today ? `${today.form_submits} of ${today.form_starts} started` : 'no day collected yet'}
                />
                <StatTile label="Script errors" value={fmt(today?.script_errors)} sub="invisible to GA4" />
              </div>
              <DayTable days={days} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Panel({ title, rows }: { title: string; rows?: RumCount[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
      </div>
      <div className="card-body">
        {rows && rows.length > 0 ? (
          <BarList rows={rows.map((r) => ({ label: r.value || '(not set)', value: r.count }))} />
        ) : (
          <div className="empty-state t-body">Nothing in the live window.</div>
        )}
      </div>
    </div>
  );
}

function DayTable({ days }: { days: RumRollup[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Day</th>
            <th className="num">Sessions</th>
            <th className="num">Page views</th>
            <th className="num">Clicks</th>
            <th className="num">Rage</th>
            <th className="num">Dead</th>
            <th className="num">Scroll</th>
            <th className="num">Forms</th>
            <th className="num">Errors</th>
          </tr>
        </thead>
        <tbody>
          {[...days].reverse().map((day) => (
            <tr key={day.period_start}>
              <td>{day.period_start}</td>
              <td className="num">{formatCompact(day.sessions)}</td>
              <td className="num">{formatCompact(day.page_views)}</td>
              <td className="num">{formatCompact(day.clicks)}</td>
              {/* The counts are always measured for a day that has a row: a real 0 shows 0, not a dash. */}
              <td className="num">{day.rage_clicks}</td>
              <td className="num">{day.dead_clicks}</td>
              <td className="num">
                {/* Null is an em dash, never 0% — nothing measured is not "nobody scrolled". */}
                {day.average_scroll_depth != null ? `${Math.round(day.average_scroll_depth)}%` : '—'}
              </td>
              <td className="num">{day.form_starts ? `${day.form_submits}/${day.form_starts}` : '—'}</td>
              <td className="num">{day.script_errors}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmt(value: number | undefined): string {
  return value === undefined ? '—' : formatCompact(value);
}
